// Whether the editor's handleUpdate pass should re-derive furniture geometry.
//
// handleUpdate does two unrelated jobs in one sweep over roomsData. It recomputes
// each room's geometry (vertices, doors, centerX/centerY) for the current
// viewport, and it re-derives every furniture item's position and size from the
// API-space values stored on the item (exactPosition / origionalWidth).
//
// Only the first job is wanted when the sweep was triggered by one room's
// furniture changing, which is what a shuffle does: furnishRoom clears the
// handleUpdate dedup key precisely so the room centers get recomputed against the
// new furniture. The second job then rewrites the furniture of every *other* room
// from scratch, and that is not a no-op. An item carrying calculateWidth divides
// by (scaleFactorX / room.scaleX), and every room that has been shuffled has had
// room.scaleX set to scaleFactorX, so the denominator collapses to 1 and the item
// is written back at a different size than modifyRoomProperties gave it. That is
// what the first-shuffled room flashes when a later room is shuffled.
//
// The furniture re-derivation only carries new information when the viewport
// transform it reads has actually moved, so gate it on a signature of those
// inputs. A shuffle leaves them all untouched; a mount, a resize, or a scale
// recalibration moves at least one.

const KEY_FIELDS = [
  "scaleFactorX",
  "scaleFactorY",
  "scaleXCanvas",
  "scaleYCanvas",
  "divWidth",
  "divHeight",
  "offsetXCheck",
  "offsetYCheck",
];

/**
 * Stable signature of the viewport inputs the furniture transform reads.
 *
 * @param {object} viewport
 * @returns {string}
 */
export function viewportTransformKey(viewport = {}) {
  return KEY_FIELDS.map((field) => String(viewport?.[field])).join("|");
}

/**
 * @param {string|null} previousKey signature from the last pass that re-derived
 * @param {object} viewport current viewport values
 * @returns {{ key: string, retransform: boolean }} key to remember, and whether
 *   this pass should re-derive furniture geometry
 */
export function resolveFurnitureRetransform(previousKey, viewport) {
  const key = viewportTransformKey(viewport);
  return { key, retransform: previousKey !== key };
}
